/**
 * An in-memory `CloudPort` for the `?ui=cloud` fixture and the Cloud Save screen's tests. It
 * behaves like the real sync as far as the screen can tell: a valid code of its own, one other
 * code that "has a save" (`FAKE_OTHER_CODE`), any other valid code answers that nothing is stored
 * under it. It touches no storage and no network.
 */

import { newCode } from '../../../platform/cloudSave/saveCode';
import type { CloudPort, CloudStatusView } from '../cloud/cloudPort';

/** Fixed bytes instead of entropy: the fixture shows the same, well-formed code every time. */
const codeFrom = (seed: number): string =>
  newCode((count) => Uint8Array.from({ length: count }, (_, i) => (seed + i * 37) & 255));

export const FAKE_OWN_CODE = codeFrom(11);
/** The code "on the other device": `useCode` accepts this one and only this one. */
export const FAKE_OTHER_CODE = codeFrom(200);

export interface FakeCloud extends CloudPort {
  /** Drive the status from a test. */
  set(status: Partial<CloudStatusView>): void;
  restarts(): number;
}

export function createFakeCloud(initial: Partial<CloudStatusView> = {}): FakeCloud {
  let status: CloudStatusView = { state: 'idle', message: '', lastSyncedAt: Date.UTC(2026, 8, 21, 9, 30), ...initial };
  let code = FAKE_OWN_CODE;
  let restarted = 0;
  const listeners = new Set<() => void>();
  const changed = (): void => {
    for (const listener of [...listeners]) listener();
  };

  return {
    status: () => status,
    code: () => (status.state === 'off' ? null : code),
    useCode(wanted) {
      if (wanted !== FAKE_OTHER_CODE) return Promise.resolve('NO SAVE IS STORED UNDER THAT CODE.');
      code = wanted;
      status = { ...status, state: 'paused', message: 'THE SAVE UNDER THAT CODE IS NOW ON THIS DEVICE.' };
      changed();
      return Promise.resolve(null);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    restart(): void {
      restarted += 1;
    },
    set(patch): void {
      status = { ...status, ...patch };
      changed();
    },
    restarts: () => restarted,
  };
}
